// กระดาษคำตอบหนึ่งแผ่น วาดเป็น SVG หน่วยมิลลิเมตร เพื่อให้ตำแหน่งที่พิมพ์ออกมาตรงกับ sheetLayout.js
// (ตัวอ่านกระดาษในขั้นสแกนใช้ตัวเลขชุดเดียวกัน) — ห้ามจัดตำแหน่งด้วย CSS/flex ในนี้
import React from 'react'
import { PAGE, markersFor, sheetVersionFor, codeCells, bubbleLayout, idLayout, frameFor } from './sheetLayout'
import { encodeSheetCode, sheetCodeText, SPARE_STUDY_ID } from './sheetCode'
import { answerLabel, labelSet } from './examUtils'

const FONT = "'THSarabun', 'Sarabun', sans-serif"

const Text = ({ x, y, size = 4.2, weight, anchor, fill = '#000', children }) => (
  <text x={x} y={y} fontSize={size} fontFamily={FONT} fontWeight={weight} textAnchor={anchor} fill={fill}>
    {children}
  </text>
)

// ช่วงเลขข้อต่อเนื่อง [1,2,3,5] -> "1–3, 5"
const ranges = (nums) => {
  const out = []
  let start = null
  let prev = null
  for (const n of nums) {
    if (start === null) start = n
    else if (n !== prev + 1) {
      out.push(start === prev ? `${start}` : `${start}–${prev}`)
      start = n
    }
    prev = n
  }
  if (start !== null) out.push(start === prev ? `${start}` : `${start}–${prev}`)
  return out.join(', ')
}

// ช่องเขียนบนกระดาษสำรอง: ป้าย + เส้นให้เขียน
const Blank = ({ x, y, label, width }) => (
  <>
    <Text x={x} y={y} size={4.6}>{label}</Text>
    <line x1={x + label.length * 1.6 + 2} y1={y + 0.8} x2={x + label.length * 1.6 + 2 + width} y2={y + 0.8}
      stroke="#000" strokeWidth="0.2" strokeDasharray="0.6 0.6" />
  </>
)

// ระยะห่างเส้นบรรทัดในพื้นที่ตอบอัตนัย (มม.) — เขียนลายมือขนาดปกติได้พอดี
const LINE_PITCH = 9

// student = นักเรียนเจ้าของแผ่น / spare = กระดาษสำรอง (ไม่มีเจ้าของ นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง)
// แบบกระดาษเลือกเอง: ข้อปรนัยพอครึ่งแผ่นก็พิมพ์ครึ่งแผ่น (sheetVersionFor) — ครึ่งล่างเป็นพื้นที่ตอบอัตนัย
// writing = พื้นที่ตอบอัตนัยของแบบครึ่งแผ่น: 'lines' มีเส้นบรรทัด / 'blank' ว่าง
export default function AnswerSheet({ exam, questions, student, room, school, spare = false, writing = 'lines' }) {
  const version = sheetVersionFor(questions, exam.exam_choices, spare)
  const rows = bubbleLayout(questions, exam.exam_choices, spare, version)
  // ตัวอักษรในวง (ก ข ค ง หรือ A B C D) เป็นแค่ป้าย — ตำแหน่งวงเหมือนกันทุกชุด ตัวอ่านไม่ได้อ่านตัวอักษร
  const labels = labelSet(exam)
  const studyId = spare ? SPARE_STUDY_ID : student.study_id
  const bits = encodeSheetCode(exam.exam_id, studyId)
  const id = spare ? idLayout(version, exam.exam_choices) : null
  const cells = codeCells(version)
  // หัวกระดาษและกรอบข้อมูลวางตามขอบเนื้อหาของแบบที่พิมพ์ (แต่ละแบบขอบซ้ายไม่เท่ากัน)
  // ส่วนนี้ตัวอ่านไม่ได้อ่าน แต่ห้ามล้ำไปทับสี่เหลี่ยมมุมหรือแถบรหัส
  const { left: L, right: R, box: B, head: HY, label: LY, labelBottom: LBY, note: NY, edge, grid: GY, half, written: WY } = frameFor(version)
  const row1 = B + 6.5
  const row2 = B + 12.5
  const codeMid = (Math.min(...cells.map((c) => c.x)) + Math.max(...cells.map((c) => c.x + c.size))) / 2
  const teacher = room.teacher_name && (
    <>
      <Text x={R - 70} y={row1} size={4.6}>ผู้สอน</Text>
      <Text x={R - 59} y={row1} size={5} weight="bold">{room.teacher_name}</Text>
    </>
  )
  const written = questions.filter((q) => q.eq_type === 'written').map((q) => q.eq_no)
  const hasTF = questions.some((q) => q.eq_type === 'truefalse')

  return (
    <svg
      className="exam-sheet"
      xmlns="http://www.w3.org/2000/svg"
      width={`${PAGE.width}mm`}
      height={`${PAGE.height}mm`}
      viewBox={`0 0 ${PAGE.width} ${PAGE.height}`}
    >
      <rect x="0" y="0" width={PAGE.width} height={PAGE.height} fill="#fff" />

      {markersFor(version).map((m) => (
        <rect key={m.corner} x={m.x} y={m.y} width={m.size} height={m.size} fill="#000" />
      ))}

      {/* หัวกระดาษ */}
      {school.logoSrc && <image href={school.logoSrc} x={L + 4} y={HY} width="16" height="16" preserveAspectRatio="xMidYMid meet" />}
      <Text x={L + 24} y={HY + 5} size={5} weight="bold">{school.school_name}</Text>
      <Text x={L + 24} y={HY + 11} size={5.4} weight="bold">กระดาษคำตอบ: {exam.exam_name}</Text>
      <Text x={L + 24} y={HY + 16.5} size={4.4}>
        วิชา {room.sub_subid} {room.sub_subname} · ภาคเรียนที่ {room.sub_semester}/{room.year_year}
      </Text>
      {/* แถวเดียวกับชื่อโรงเรียน ไม่ใช่แถววิชา — ชื่อวิชายาวชนได้ */}
      {spare && <Text x={R - 4} y={HY + 6} size={5.6} weight="bold" anchor="end">กระดาษสำรอง</Text>}

      <rect x={L} y={B} width={R - L} height="15" rx="1.5" fill="none" stroke="#000" strokeWidth="0.3" />
      {spare ? (
        // กระดาษสำรอง — ไม่พิมพ์ชื่อใคร นักเรียนเขียนเอง (ครูใช้ชื่อที่เขียนเทียบกับเลขที่ฝนตอนสแกน)
        <>
          {/* เส้นชื่อหยุดก่อนช่องผู้สอน — กระดาษสำรองก็พิมพ์ชื่อผู้สอนของห้องที่สั่งพิมพ์ */}
          <Blank x={L + 4} y={row1} label="ชื่อ-สกุล" width={R - 74 - (L + 21)} />
          {teacher}
          <Blank x={L + 4} y={row2 + 0.5} label="เลขประจำตัว" width={24} />
          <Blank x={L + 57} y={row2 + 0.5} label="ห้อง ม." width={16} />
          <Blank x={L + 92} y={row2 + 0.5} label="เลขที่" width={12} />
          <Text x={R - 4} y={row2 + 0.5} size={3.6} anchor="end" fill="#444">ฝนเลขประจำตัวที่คอลัมน์ขวาด้วย</Text>
        </>
      ) : (
      /* ข้อมูลนักเรียน — พิมพ์ให้แล้ว นักเรียนไม่ต้องเขียน */
      <>
      <Text x={L + 4} y={row1} size={4.6}>ชื่อ-สกุล</Text>
      <Text x={L + 20} y={row1} size={5} weight="bold">{student.st_name}</Text>
      {teacher}
      <Text x={L + 4} y={row2} size={4.6}>เลขประจำตัว</Text>
      <Text x={L + 24} y={row2} size={5} weight="bold">{student.st_idnumber}</Text>
      <Text x={L + 72} y={row2} size={4.6}>ห้อง</Text>
      <Text x={L + 80} y={row2} size={5} weight="bold">ม.{room.c_id}/{room.r_no}</Text>
      <Text x={L + 112} y={row2} size={4.6}>เลขที่</Text>
      <Text x={L + 122} y={row2} size={5} weight="bold">{student.seat}</Text>
      </>
      )}

      {/* แถบรหัสแผ่น — แบบขอบกระดาษพิมพ์เฉพาะช่องทึบ ไม่มีกรอบช่องว่างให้นักเรียนฝนเล่น
          และไม่มีข้อความชิดแถบ (ข้างนอกแถบคือเส้นที่ตัวอ่านวัดความขาว) */}
      {!edge && (
        <Text x={codeMid} y={cells[0].y - 2} size={3.6} anchor="middle" fill="#444">
          รหัสแผ่น — ห้ามขีดเขียนหรือพับบริเวณนี้
        </Text>
      )}
      {cells.map((c) => (edge && !bits[c.index] ? null : (
        <rect
          key={c.index}
          x={c.x}
          y={c.y}
          width={c.size}
          height={c.size}
          fill={bits[c.index] ? '#000' : 'none'}
          stroke="#000"
          strokeWidth="0.2"
        />
      )))}
      <Text x={codeMid} y={LY} size={3.4} anchor="middle" fill="#444">
        {edge ? 'แถบดำด้านบนคือรหัสแผ่น ห้ามขีดเขียน · ' : 'รหัสแผ่น '}{sheetCodeText(exam.exam_id, studyId)} · แบบ {version}
      </Text>
      {LBY && (
        <Text x={Math.max(...cells.map((c) => c.x + c.size))} y={LBY} size={3.4} anchor="end" fill="#444">
          แถบดำด้านล่างคือรหัสแผ่น ห้ามขีดเขียน
        </Text>
      )}

      {/* คำชี้แจง — เน้นให้ฝนทึบ: กากบาทมีหมึกน้อยเกินเกณฑ์ ตัวอ่านอ่านไม่ได้หลายข้อ */}
      <Text x={L} y={GY - 6} size={4}>
        <tspan fontWeight="bold">ฝนด้วยดินสอให้ดำทึบเต็มวง ห้ามกากบาท</tspan>
        {' · ข้อละวงเดียว · เปลี่ยนคำตอบให้ลบให้สะอาด'}
        {hasTF ? ` · ข้อใช่/ไม่ใช่: ${labels.tfHelp}` : ''}
      </Text>

      {/* ช่องฝน */}
      {rows.map((row) => (
        <g key={row.eq_no}>
          <Text x={row.numberX} y={row.cy + 1.4} size={4.2} weight="bold" anchor="end">{row.eq_no}</Text>
          {row.bubbles.map((b) => (
            <g key={b.value}>
              <circle cx={b.cx} cy={b.cy} r={b.r} fill="none" stroke="#000" strokeWidth="0.25" />
              <Text x={b.cx} y={b.cy + 1.1} size={3} anchor="middle" fill="#999">{answerLabel(b.value, labels)}</Text>
            </g>
          ))}
        </g>
      ))}

      {/* กระดาษสำรอง: ช่องเขียน + ช่องฝนเลขประจำตัว 5 หลัก (คอลัมน์สุดท้ายของตาราง ดู sheetLayout.idLayout) */}
      {id && (
        <g>
          <Text x={id.titleX} y={id.titleY} size={4.2} weight="bold" anchor={id.titleAnchor}>เลขประจำตัว</Text>
          {id.boxes.map((b, i) => (
            <rect key={i} x={b.x} y={b.y} width={b.size} height={b.size} fill="none" stroke="#000" strokeWidth="0.25" />
          ))}
          {id.digits.map((d) => d.bubbles.map((b) => (
            <g key={`${d.index}-${b.value}`}>
              <circle cx={b.cx} cy={b.cy} r={b.r} fill="none" stroke="#000" strokeWidth="0.25" />
              <Text x={b.cx} y={b.cy + 1.1} size={3} anchor="middle" fill="#999">{b.label}</Text>
            </g>
          )))}
        </g>
      )}

      {half && written.length > 0 && (
        // ครึ่งล่าง: พื้นที่ตอบอัตนัย — ตัวอ่านไม่ได้อ่านส่วนนี้ (อยู่นอกสี่เหลี่ยมมุม) ครูตรวจเองแล้วกรอกคะแนน
        <g>
          <line x1={L} y1={WY} x2={R} y2={WY} stroke="#000" strokeWidth="0.3" />
          <Text x={L} y={WY + 7} size={5} weight="bold">ตอบข้ออัตนัย ข้อ {ranges(written)}</Text>
          <Text x={R} y={WY + 7} size={3.8} anchor="end" fill="#444">เขียนเลขข้อกำกับทุกข้อ · ไม่พอเขียนต่อด้านหลัง</Text>
          {writing === 'lines' && Array.from(
            { length: Math.floor((PAGE.height - 12 - (WY + 17)) / LINE_PITCH) + 1 },
            (_, i) => WY + 17 + i * LINE_PITCH,
          ).map((y) => (
            <line key={y} x1={L} y1={y} x2={R} y2={y} stroke="#999" strokeWidth="0.2" />
          ))}
        </g>
      )}

      {!half && written.length > 0 && (
        // เว้นจากสี่เหลี่ยมมุมล่างซ้าย — ตัวอักษรที่ติดกันจะถูกนับเป็นก้อนเดียวกับสี่เหลี่ยมแล้วหามุมไม่เจอ
        <Text x={L + 4} y={NY} size={4}>
          ข้อ {ranges(written)} เป็นอัตนัย ตอบในกระดาษแยก
        </Text>
      )}
    </svg>
  )
}
